import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, PasswordInput, PinInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import * as authApi from '@/features/auth/api';
import { passwordSchema } from '@/features/auth/password-utils';
import { userFacingMessage } from '@/shared/api/errorMessage';

interface UpdSearch { email?: string }

export const Route = createFileRoute('/_auth/update-password')({
  validateSearch: (s: Record<string, unknown>): UpdSearch => ({
    email: typeof s.email === 'string' ? s.email : undefined,
  }),
  component: UpdatePage,
});

const schema = z.object({ password: passwordSchema, confirm: z.string() }).refine((d) => d.password === d.confirm, {
  path: ['confirm'],
  message: 'Le password non coincidono',
});

function UpdatePage() {
  const navigate = useNavigate();
  const { email: emailParam } = Route.useSearch();
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState(emailParam ?? '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useForm({
    mode: 'controlled',
    initialValues: { password: '', confirm: '' },
    validate: schemaResolver(schema, { sync: true }),
  });

  const onSubmit = form.onSubmit(async ({ password }) => {
    const emailCheck = z.string().email('Email non valida').safeParse(email);
    if (!emailCheck.success) {
      setEmailError(email ? 'Email non valida' : 'Inserisci la tua email');
      return;
    }
    if (otp.length < 6) {
      notifications.show({ color: 'yellow', message: 'Inserisci il codice di 6 cifre' });
      return;
    }
    setBusy(true);
    try {
      await authApi.resetPasswordWithOtp(email, otp, password);
      notifications.show({ color: 'green', message: 'Password aggiornata' });
      void navigate({ to: '/login' });
    } catch (err) {
      const msg = userFacingMessage(err, 'Errore');
      notifications.show({ color: 'red', title: 'Reset fallito', message: msg });
    } finally {
      setBusy(false);
    }
  });

  return (
    <Stack>
      <Title order={2}>Imposta nuova password</Title>
      <Text c="dimmed" size="sm">Inserisci il codice ricevuto via email e la nuova password.</Text>
      {!emailParam && (
        <TextInput
          label="Email"
          required
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.currentTarget.value);
            setEmailError(null);
          }}
          error={emailError}
        />
      )}
      <Stack align="center">
        <Text size="sm">Codice OTP</Text>
        <PinInput length={6} type="number" oneTimeCode value={otp} onChange={setOtp} />
      </Stack>
      <form onSubmit={onSubmit}>
        <Stack>
          <PasswordInput label="Nuova password" required {...form.getInputProps('password')} />
          <PasswordInput label="Conferma password" required {...form.getInputProps('confirm')} />
          <Button type="submit" loading={busy} fullWidth size="md" disabled={otp.length < 6 || !email}>
            Aggiorna password
          </Button>
        </Stack>
      </form>
    </Stack>
  );
}
